import { useState, useRef, useEffect } from 'react';
import { Info, X } from 'lucide-react';
import { Link } from 'react-router-dom';

// Get certainty label from sigma value
// Returns object with main label and optional subtitle for "New"
export const getCertaintyLabel = (sigma, includeSubtitle = false) => {
  if (sigma == null) return includeSubtitle ? { main: null, subtitle: null } : null;
  
  if (sigma >= 7.1) {
    return includeSubtitle ? { main: 'New', subtitle: 'Very Low Certainty' } : 'New';
  }
  if (sigma >= 5.9 && sigma <= 7.0) {
    return includeSubtitle ? { main: 'Low Certainty', subtitle: null } : 'Low Certainty';
  }
  if (sigma >= 4.1 && sigma <= 5.8) {
    return includeSubtitle ? { main: 'Medium Certainty', subtitle: null } : 'Medium Certainty';
  }
  if (sigma >= 3.1 && sigma <= 4.0) {
    return includeSubtitle ? { main: 'High Certainty', subtitle: null } : 'High Certainty';
  }
  if (sigma >= 2.5 && sigma <= 3.0) {
    return includeSubtitle ? { main: 'Very High Certainty', subtitle: null } : 'Very High Certainty';
  }
  return includeSubtitle ? { main: null, subtitle: null } : null;
};

// Format level (mu/5) with 2 decimals
export const formatLevel = (mu) => {
  if (mu == null) return null;
  return (mu / 5).toFixed(2);
};

// Info tooltip for Level & Certainty
export const LevelCertaintyInfo = ({ language }) => {
  const [isOpen, setIsOpen] = useState(false);
  const tooltipRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const content = language === 'el' ? {
    levelLabel: 'Level:',
    levelDesc: 'Το τρέχον επίπεδό σου',
    certaintyLabel: 'Certainty:',
    certaintyDesc: 'Πόσο αξιόπιστο είναι (περισσότεροι αγώνες = μεγαλύτερη σταθερότητα)',
    learnMore: 'Δες πώς λειτουργεί →'
  } : {
    levelLabel: 'Level:',
    levelDesc: 'Your current playing level',
    certaintyLabel: 'Certainty:',
    certaintyDesc: 'How reliable it is (more matches = more stable)',
    learnMore: 'Learn how it works →'
  };

  return (
    <div className="relative inline-block" ref={tooltipRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="text-muted-foreground/60 hover:text-muted-foreground transition-colors p-0.5"
        aria-label="Info about Level and Certainty"
      >
        <Info className="h-3 w-3" />
      </button>
      
      {isOpen && (
        <div className="fixed sm:absolute z-50 sm:bottom-full left-4 right-4 sm:left-auto sm:right-0 bottom-auto top-20 sm:top-auto sm:mb-2 w-auto sm:w-64 bg-popover border border-border rounded-lg shadow-lg p-3 text-sm">
          <button
            onClick={() => setIsOpen(false)}
            className="absolute top-2 right-2 text-muted-foreground hover:text-foreground"
          >
            <X className="h-3 w-3" />
          </button>
          
          <div className="space-y-2 pr-4">
            <div>
              <span className="font-medium text-foreground">{content.levelLabel}</span>
              <span className="text-muted-foreground ml-1">{content.levelDesc}</span>
            </div>
            <div>
              <span className="font-medium text-foreground">{content.certaintyLabel}</span>
              <span className="text-muted-foreground ml-1">{content.certaintyDesc}</span>
            </div>
            <Link
              to="/rules?tab=level"
              className="block text-primary hover:underline mt-2 text-xs"
              onClick={() => setIsOpen(false)}
            >
              {content.learnMore}
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

// Display Level and Certainty inline
export const LevelCertaintyDisplay = ({ mu, sigma, language, showInfo = false, className = '' }) => {
  const level = formatLevel(mu);
  const certainty = getCertaintyLabel(sigma);
  
  if (!level && !certainty) return null;
  
  return (
    <span className={`text-muted-foreground ${className}`}>
      {level && <span>{level}</span>}
      {level && certainty && <span className="mx-1">•</span>}
      {certainty && <span>{certainty}</span>}
      {showInfo && (level || certainty) && (
        <span className="ml-1 inline-flex align-middle">
          <LevelCertaintyInfo language={language} />
        </span>
      )}
    </span>
  );
};
